/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback } from "react";
import ReactECharts from "echarts-for-react";

import { useRef, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { COLORS } from "../../utils/constants";
import CardLayout from "../Cards/CardLayout";
import CardError from "../feedBacks/CardError";
import Loader from "../feedBacks/loader";
import NoContent from "../feedBacks/NoContent";
import { IHierarchicalNode } from "../../utils/interfaces";
import AddLevelModal from "../Modals/AddLevel";
import DeleteConfirmationModal from "../Modals/DeleteModal";
import { useUtils } from "../../customHooks/useUtils";

function HierarchicalChart({
  data,
  isLoading,
  error,
  company_id,
}: {
  company_id: string;
  data: IHierarchicalNode[];
  isLoading: boolean;
  error: string;
  }) {
  const { FEEDBACK_MESSAGES } = useUtils();
  const chartRef = useRef<ReactECharts>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [chartReady, setChartReady] = useState(false);
  const navigate = useNavigate();
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const chartInstanceRef = useRef<any>(null);
  const [addLevelModalOpen, setAddLevelModalOpen] = useState(false);
  const [editLevelModalOpen, setEditLevelModalOpen] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState<IHierarchicalNode | null>(
    null
  );
  const [deleteLevelModalOpen, setDeleteLevelModalOpen] = useState(false);
  const [deleteLevelId, setDeleteLevelId] = useState<string | null>(null);

  // Function to transform the API data into ECharts compatible format
  const transformToEChartsFormat = (data: IHierarchicalNode[]): any => {
    const transform = (node: IHierarchicalNode): any => {
      // Create the node itself
      const result: any = {
          name: node.name,
          level_name: node.level_name,
        id: node.id,
        itemStyle: {
          color: COLORS.BLACK_LIGHTER,
        },
      };

      const children: any[] = [];

      // Add regular children nodes
      if (node.children && node.children.length > 0) {
        children.push(...node.children.map(transform));
      }

      // Add device nodes as children if any exist
      if (node.devices && node.devices.length > 0) {
        node.devices.forEach((device) => {
          children.push({
            name: device.asset_name,
            id: device.id,
            value: device.asset_capacity,
            itemStyle: {
              color: COLORS.PRIMARY,
            },
            // Include device details for the tooltip
            deviceInfo: {
              type: device.asset_type,
              capacity: device.asset_capacity,
              year: device.transformer_year,
              nature: device.nature_of_transformer,
              gateway: device.gateway_serial,
              active: device.is_active,
            },
          });
        });

        // Highlight the parent node if it has devices
        result.itemStyle.color = COLORS.PRIMARY;
      }

      if (children.length > 0) {
        result.children = children;
      }

      return result;
    };

    return data?.map(transform) || [];
  };

  // Use transformed data for the chart - wrapped in useMemo to avoid dependency changes on every render
  const chartData = useMemo(() => {
    return data ? transformToEChartsFormat(data as IHierarchicalNode[]) : [];
  }, [data]);

  // Create and configure the chart options
  const options = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return null;
    }

    return {
      tooltip: {
        trigger: "item",
        triggerOn: "mousemove",
        alwaysShowContent: false,
        enterable: true,
        formatter: function (params: any) {
          const { name, data } = params;

          // Enhanced tooltip to show device information when available
          if (data && data.deviceInfo) {
            // This is a device node
            const { type, capacity, year, nature, gateway, active } =
              data.deviceInfo;
            const assetId = data.id;
            return `
              <div style="padding: 5px">
                <div><strong>${name}</strong> (${type})</div>
                <div>Capacity: ${capacity}</div>
                <div>Year: ${year}</div>
                <div>Nature: ${nature}</div>
                <div>Gateway: ${gateway}</div>
                <div>Status: ${active ? "Active" : "Inactive"}</div>
                <div style="margin-top: 8px;">
                  <a href="/dashboard/operations/site/${assetId}" data-asset-id="${assetId}" class="dashboard-link" style="color: #F59E0B; font-weight: 500; cursor: pointer; text-decoration: underline; display: flex; align-items: center;">
                    View Dashboard <span style="margin-left: 4px;">&#8599;</span>
                  </a>
                </div>
              </div>
            `;
          } else {
            const nodeId = data.id;
            return `
              <div style="padding: 10px">
                <div><strong>${name}</strong></div>
                <div style="margin-top: 8px;">
                  <div 
                    class="edit-level-btn" 
                    data-node-id="${nodeId}" 
                    style="color: #3B82F6; font-weight: 500; cursor: pointer; padding: 4px 0; display: flex; align-items: center;"
                  >
                    <span style="margin-right: 4px;">✏️</span> Edit
                  </div>
                  <div 
                    class="delete-level-btn" 
                    data-node-id="${nodeId}" 
                    style="color: #EF4444; font-weight: 500; cursor: pointer; padding: 4px 0; display: flex; align-items: center;"
                  >
                    <span style="margin-right: 4px;">🗑️</span> Delete
                  </div>
                </div>
              </div>
            `;
          }
        },
      },
      series: [
        {
          type: "tree",
          name: "Network Hierarchy",
          data: chartData,
          top: "10%",
          left: "8%",
          bottom: "22%",
          right: "20%",
          symbolSize: 10,
          orient: "vertical",
          edgeShape: "polyline",
          edgeForkPosition: "63%",
          initialTreeDepth: 3,
          lineStyle: {
            width: 2,
          },
          label: {
            backgroundColor: "#fff",
            position: "top",
            verticalAlign: "middle",
            align: "center",
            fontSize: 12,
            distance: 5,
          },
          leaves: {
            label: {
              position: "right",
              verticalAlign: "middle",
              align: "left",
            },
          },
          emphasis: {
            focus: "descendant",
          },
          expandAndCollapse: true,
          animationDuration: 200,
          animationDurationUpdate: 300,
          roam: true, // Enable panning and zooming
        },
      ],
    };
  }, [chartData]);

  const findNodeById = useCallback((nodes: any[], id: string | null): any => {
    if (!id) return null;

    for (const node of nodes) {
      if (node.id === id) {
        return node;
      }

      if (node.children && node.children.length > 0) {
        const found = findNodeById(node.children, id);
        if (found) return found;
      }
    }

    return null;
  }, []);

  // Function to handle edit level action - wrapped in useCallback
  const handleEditLevel = useCallback(
    (nodeData: IHierarchicalNode) => {
      setSelectedLevel(nodeData);
      setEditLevelModalOpen(true);
    },
    []
  );

  // Function to handle delete level action - wrapped in useCallback
  const handleDeleteLevel = useCallback((nodeId: string) => {
    setDeleteLevelId(nodeId);
    setDeleteLevelModalOpen(true);
  }, []);
  // Set up the chart event listeners
  useEffect(() => {
    if (chartInstanceRef.current) {
    //   const instance = chartInstanceRef.current;

      // Listen for tooltip DOM element click events
      document.addEventListener("click", (event) => {
        const target = event.target as HTMLElement;

        // Check if the click is on the edit button
        if (target.classList.contains("edit-level-btn")) {
          const nodeId = target.getAttribute("data-node-id");
          const nodeData = findNodeById(chartData, nodeId);
          if (nodeData) {
            handleEditLevel(nodeData);
          }
        }

        // Check if the click is on the delete button
        if (target.classList.contains("delete-level-btn")) {
          const nodeId = target.getAttribute("data-node-id");
          if (nodeId) {
            handleDeleteLevel(nodeId);
          }
        }

        // Check if the click is on the dashboard link
        if (target.classList.contains("dashboard-link")) {
          const assetId = target.getAttribute("data-asset-id");
          if (assetId) {
            navigate(`/dashboard/operations/site/${assetId}`);
          }
        }
      });
    }
  }, [findNodeById, handleDeleteLevel, handleEditLevel, navigate, chartData]);

  // Initialize chart when component mounts
  useEffect(() => {
    // Function to perform chart initialization
    const initializeChart = () => {
      if (!chartRef.current) return;

      const chartInstance = chartRef.current.getEchartsInstance();
      if (!chartInstance) return;

      chartInstanceRef.current = chartInstance;

      // Wait a bit to ensure DOM is ready
      setTimeout(() => {
        if (chartInstanceRef.current) {
          chartInstanceRef.current.resize();
          setChartReady(true);
        }
      }, 100);
    };

    if (chartRef.current && !isLoading && chartData.length > 0) {
      initializeChart();
    }

    // Add window resize handler
    const handleResize = () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.resize();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isLoading, chartData]);

  // Handle click on node to toggle fixed tooltip and handle dashboard navigation
  useEffect(() => {
    if (!chartReady || !chartRef.current) return;

    const chartInstance = chartInstanceRef.current;
    if (!chartInstance) return;

    // Clear previous event listeners to prevent duplicates
    chartInstance.off("click");
    chartInstance.off("treeexpand");
    chartInstance.off("treecollapse");

    // Add click event listener with debounce to avoid double-triggering
    chartInstance.on("click", (params: any) => {
      // Clear any pending click to avoid double processing
      if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);

      // Small delay to avoid race conditions in production
      clickTimeoutRef.current = setTimeout(() => {
        if (!params || !params.data) return;

        const nodeId = params.data.id;

        // Toggle selected node
        if (selectedNodeId === nodeId) {
          setSelectedNodeId(null);
          chartInstance.dispatchAction({
            type: "hideTip",
          });
        } else {
          setSelectedNodeId(nodeId);

          // Use the params directly from the click event
          chartInstance.dispatchAction({
            type: "showTip",
            seriesIndex: params.seriesIndex,
            dataIndex: params.dataIndex,
          });
        }
      }, 50);
    });

    // Handle node expanding
    chartInstance.on("treeexpand", () => {
      // Force layout recalculation after expansion
      setTimeout(() => {
        chartInstance.resize();
      }, 300);
    });

    // Handle node collapsing
    chartInstance.on("treecollapse", () => {
      // Force layout recalculation after collapse
      setTimeout(() => {
        chartInstance.resize();
      }, 300);
    });

    // Handle clicking on the dashboard link inside tooltip
    const handleTooltipLinkClick = (event: MouseEvent) => {
      // Check if the click was on a link inside the tooltip
      const target = event.target as HTMLElement;
      const link = target.closest(".dashboard-link") as HTMLAnchorElement;

      if (link && link.closest(".echarts-tooltip")) {
        event.preventDefault();

        // Check if it's an asset or layer link
        const assetId = link.getAttribute("data-asset-id");
        const layerId = link.getAttribute("data-layer-id");

        if (assetId) {
          navigate(`/dashboard/operations/site/${assetId}`);
        } else if (layerId) {
          navigate(`/dashboard/operations/sites/aggregate/${layerId}`);
        }
      }
    };

    document.addEventListener("click", handleTooltipLinkClick);

    return () => {
      // Clean up all event listeners
      document.removeEventListener("click", handleTooltipLinkClick);
      if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
      if (chartInstance) {
        chartInstance.off("click");
        chartInstance.off("treeexpand");
        chartInstance.off("treecollapse");
      }
    };
  }, [chartReady, selectedNodeId, navigate]);

  // Persist tooltip for selected node and handle node expansion
  useEffect(() => {
    if (!selectedNodeId || !chartReady || !chartInstanceRef.current) return;

    const chart = chartInstanceRef.current;

    // Get all nodes data with a more robust flattening algorithm
    const flattenNodes = (nodes: any[]): any[] => {
      if (!nodes || !Array.isArray(nodes)) return [];

      let result: any[] = [];

      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        if (node) {
          result.push(node);
          if (node.children && Array.isArray(node.children)) {
            result = result.concat(flattenNodes(node.children));
          }
        }
      }

      return result;
    };

    const attemptToSelectNode = () => {
      try {
        const allNodes = flattenNodes(chartData);

        // Find the selected node
        const selectedNode = allNodes.find(
          (node) => node && node.id === selectedNodeId
        );

        if (selectedNode) {
          // Approach 1: Use name to find the node (more reliable in most cases)
          chart.dispatchAction({
            type: "highlight",
            name: selectedNode.name,
          });

          chart.dispatchAction({
            type: "showTip",
            name: selectedNode.name,
          });

          // Approach 2: As a fallback, try to expand all parent nodes to ensure visibility
          if (selectedNode.parentId) {
            const parentNode = allNodes.find(
              (node) => node && node.id === selectedNode.parentId
            );
            if (parentNode) {
              chart.dispatchAction({
                type: "treeExpandTo",
                name: parentNode.name,
              });
            }
          }
        }
      } catch (err) {
        console.error("Error selecting node:", err);
      }
    };

    // Try once with a delay to handle production timing issues
    setTimeout(attemptToSelectNode, 300);
  }, [chartData, selectedNodeId, chartReady]);

  // Handle chart updates when data changes
  useEffect(() => {
    if (chartReady && chartInstanceRef.current && options) {
      chartInstanceRef.current.setOption(options, true);
    }
  }, [options, chartReady]);

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-full w-full">
          <Loader />
        </div>
      );
    } else if (error) {
      return (
        <CardError message={FEEDBACK_MESSAGES.ERROR} className="!h-[100px]" />
      );
    } else if (!data || !chartData || chartData.length === 0) {
      return <NoContent message={FEEDBACK_MESSAGES.NO_DATA} />;
    } else {
      return (
        <div className="w-full h-[600px]">
          <ReactECharts
            ref={chartRef}
            option={options || { series: [] }}
            style={{ height: "100%", width: "100%" }}
            notMerge={false} // Changed to false to prevent flashing
            lazyUpdate={true} // Changed to true for better performance
            opts={{
              renderer: "canvas",
              devicePixelRatio: window.devicePixelRatio || 1,
            }}
            onChartReady={() => {
              setChartReady(true);
              if (chartRef.current) {
                chartInstanceRef.current =
                  chartRef.current.getEchartsInstance();
                // Ensure proper initial sizing
                setTimeout(() => {
                  if (chartInstanceRef.current) {
                    chartInstanceRef.current.resize();
                  }
                }, 50);
              }
            }}
          />
        </div>
      );
    }
  };

  return (
    <CardLayout title="" className="min-w-[500px] flex-1">
      <div className="flex flex-col h-full w-full relative">
        {chartData && chartData.length > 0 && (
          <div className="absolute top-[-5%] right-0 flex gap-2 justify-between z-10">
            <div className="flex-1 flex gap-2 justify-end">
              <div className="flex items-center gap-1 rounded-full bg-primary-blackLighter2 px-3 py-2">
                <span
                  className="w-4 h-4 rounded-md"
                  style={{ backgroundColor: COLORS.PRIMARY }}
                ></span>
                <span className="text-[11px]">With Devices</span>
              </div>
              <div className="flex items-center gap-1 rounded-full bg-primary-blackLighter2 px-3 py-2">
                <span
                  className="w-4 h-4 rounded-md"
                  style={{ backgroundColor: COLORS.BLACK_LIGHTER }}
                ></span>
                <span className="text-[11px]">No Devices</span>
              </div>
            </div>
          </div>
        )}
        {renderBody()}
      </div>
      {addLevelModalOpen && (
        <AddLevelModal
          company_id={company_id}
          isModalOpen={addLevelModalOpen}
          closeModal={() => setAddLevelModalOpen(false)}
          enableOutsideClick={false}
          type="add"
        />
      )}
      {editLevelModalOpen && (
        <AddLevelModal
          data={selectedLevel as IHierarchicalNode}
          isModalOpen={editLevelModalOpen}
          closeModal={() => setEditLevelModalOpen(false)}
          enableOutsideClick={false}
          type="edit"
        />
      )}
      {deleteLevelModalOpen && (
        <DeleteConfirmationModal
          isModalOpen={deleteLevelModalOpen}
          closeModal={() => setDeleteLevelModalOpen(false)}
          enableOutsideClick={true}
          itemId={deleteLevelId as string}
          instruction="Are you sure you want to  delete this level"
          successMessage="Level deleted successfully"
          errorMessage="Error deleting LEvel"
          route={`/classifications/${deleteLevelId}`}
          invalidateQueryKey={["/classifications", "fetchAssetClassifications", company_id]}
        />
      )}
    </CardLayout>
  );
}

export default HierarchicalChart;
